import { useEffect, useState } from "react";
import { Mail, ChevronDown, ChevronRight, ExternalLink, Eye, MousePointerClick, Reply } from "lucide-react";
import DOMPurify from "dompurify";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ComposeEmailModal from "./ComposeEmailModal";
import EmailDraftsPanel from "./EmailDraftsPanel";

function fmtTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return `${date} · ${time}`;
  } catch { return iso; }
}

function TrackingBadges({ token }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get(`/email/tracking/${encodeURIComponent(token)}`);
        if (alive) setStats(data);
      } catch { /* 404 or 401 — fine, just skip */ }
    })();
    return () => { alive = false; };
  }, [token]);
  if (!token || !stats) return null;
  const opens = stats.open_count || 0;
  const clicks = stats.click_count || 0;
  const openTitle = opens
    ? `Opened ${opens} time${opens === 1 ? "" : "s"} — last ${fmtTs(stats.last_opened_at)}`
    : "Not opened yet";
  const clickTitle = clicks
    ? `Clicked ${clicks} time${clicks === 1 ? "" : "s"} — last ${fmtTs(stats.last_clicked_at)}`
    : "No link clicks";
  return (
    <span style={{ display: "inline-flex", gap: 8, marginLeft: 8, verticalAlign: "middle" }} data-testid="email-tracking-badges">
      <span
        title={openTitle}
        data-testid="email-tracking-opens"
        style={{
          display: "inline-flex", alignItems: "center", gap: 3,
          fontSize: 11, padding: "1px 6px", borderRadius: 999,
          background: opens ? "rgba(52,168,83,0.12)" : "var(--pg-surface-2)",
          color: opens ? "var(--pg-accent-deep, #34a853)" : "var(--pg-text-mute)",
          border: `1px solid ${opens ? "rgba(52,168,83,0.28)" : "var(--pg-border)"}`,
        }}
      >
        <Eye size={11} strokeWidth={2.4} /> {opens}
      </span>
      <span
        title={clickTitle}
        data-testid="email-tracking-clicks"
        style={{
          display: "inline-flex", alignItems: "center", gap: 3,
          fontSize: 11, padding: "1px 6px", borderRadius: 999,
          background: clicks ? "rgba(91,79,232,0.12)" : "var(--pg-surface-2)",
          color: clicks ? "var(--pg-purple, #5B4FE8)" : "var(--pg-text-mute)",
          border: `1px solid ${clicks ? "rgba(91,79,232,0.28)" : "var(--pg-border)"}`,
        }}
      >
        <MousePointerClick size={11} strokeWidth={2.4} /> {clicks}
      </span>
    </span>
  );
}

function stripReply(subject) {
  return (subject || "").replace(/^\s*(re|fwd|fw)\s*:\s*/i, "").trim();
}

// Return "Alex Buyer <alex@acme.com>, bob@acme.com" minus the current user's
// email so a reply-all doesn't send them a copy of their own message.
function pruneAddresses(raw, selfEmail) {
  if (!raw) return "";
  const self = (selfEmail || "").toLowerCase().trim();
  return raw
    .split(/,\s*/)
    .map(s => s.trim())
    .filter(s => {
      if (!s) return false;
      // Extract address portion from "Name <addr>" or bare addr
      const m = s.match(/<([^>]+)>/);
      const addr = (m ? m[1] : s).toLowerCase();
      return addr !== self;
    })
    .join(", ");
}

// Build the quoted-email HTML block Gmail-style so the recipient sees context.
function buildQuotedBlock(email) {
  const ts = email.timestamp || email.date || "";
  const fromLine = email.from ? `On ${ts ? fmtTs(ts) + ", " : ""}${email.from} wrote:` : "";
  const body = email.body_html
    ? DOMPurify.sanitize(email.body_html, {
        FORBID_TAGS: ["style", "script", "iframe", "object", "embed", "form"],
        FORBID_ATTR: ["style", "onerror", "onload", "onclick"],
      })
    : `<p>${(email.body_text || email.snippet || "").replace(/\n/g, "<br>")}</p>`;
  return (
    `<div class="gmail_quote" style="border-left:2px solid #cbd5e1;margin:12px 0 0;padding:0 0 0 12px;color:#475569;font-size:13px;">` +
    (fromLine ? `<div style="color:#64748b;margin-bottom:6px;">${fromLine}</div>` : "") +
    body +
    `</div>`
  );
}

function EmailEntry({ e, onReplyClick }) {
  const [open, setOpen] = useState(false);
  const email = e.email || {};
  const isSent = email.direction === "sent";
  const trackingToken = email.tracking_token || e.tracking_token;
  const canReply = !!(email.provider === "google" && email.provider_message_id);
  return (
    <div
      className="rounded-lg p-3 mt-1"
      style={{
        background: "var(--pg-surface-soft, rgba(0,0,0,0.02))",
        border: "1px solid var(--pg-border)",
      }}
      data-testid="history-email-entry"
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex-1 flex items-start gap-3 text-left min-w-0"
          style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
        >
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: isSent ? "rgba(91,79,232,0.12)" : "rgba(234,67,53,0.10)",
          color: isSent ? "var(--pg-purple)" : "#EA4335",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Mail size={15} />
        </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate" style={{ color: "var(--pg-text)" }}>
              <strong>{isSent ? "Sent" : "Received"}</strong>
              <span style={{ color: "var(--pg-text-mute)" }}> · </span>
              {email.subject || "(no subject)"}
              {isSent && trackingToken && <TrackingBadges token={trackingToken} />}
            </div>
            <div className="text-xs mt-0.5 truncate" style={{ color: "var(--pg-text-mute)" }}>
              {isSent ? `To ${email.to}` : `From ${email.from}`}
              {email.snippet ? ` · ${email.snippet.slice(0, 90)}` : ""}
            </div>
          </div>
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginTop: 4 }}>
          {canReply && (
            <button
              type="button"
              onClick={(evt) => { evt.stopPropagation(); onReplyClick(email, e); }}
              data-testid="history-email-reply"
              title="Reply all to this thread"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                fontSize: 11, fontWeight: 500,
                padding: "4px 9px", borderRadius: 6,
                background: "var(--pg-surface)",
                border: "1px solid var(--pg-border)",
                color: "var(--pg-text)",
                cursor: "pointer",
              }}
            >
              <Reply size={11} strokeWidth={2.2} /> Reply
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            data-testid="history-email-toggle"
            style={{
              background: "none", border: "none", padding: 4,
              cursor: "pointer", color: "var(--pg-text-mute)",
              display: "flex", alignItems: "center",
            }}
          >
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-3 pl-11" style={{ borderTop: "1px solid var(--pg-border)", paddingTop: 10 }}>
          <div className="text-xs mb-2" style={{ color: "var(--pg-text-mute)" }}>
            <div><strong>From:</strong> {email.from}</div>
            <div><strong>To:</strong> {email.to}</div>
            {email.cc && <div><strong>Cc:</strong> {email.cc}</div>}
          </div>
          {email.body_html ? (
            <div
              className="text-sm"
              style={{ color: "var(--pg-text)", lineHeight: 1.55, wordBreak: "break-word" }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.body_html, {
                ADD_ATTR: ["target", "rel"],
                FORBID_TAGS: ["style", "script", "iframe", "object", "embed", "form"],
                FORBID_ATTR: ["style", "onerror", "onload", "onclick"],
              }) }}
            />
          ) : (
            <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--pg-text)", lineHeight: 1.55 }}>
              {email.body_text || email.snippet || "(no body)"}
            </div>
          )}
          {email.attachments && email.attachments.length > 0 && (
            <div className="text-xs mt-3" style={{ color: "var(--pg-text-mute)" }}>
              📎 {email.attachments.length} attachment{email.attachments.length === 1 ? "" : "s"}:
              {" "}
              {email.attachments.map(a => a.filename).join(", ")}
            </div>
          )}
          {email.link && (
            <a
              href={email.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-3 text-xs"
              style={{ color: "var(--pg-purple)" }}
              data-testid="history-email-open-gmail"
            >
              Open in Gmail <ExternalLink size={11} />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default function HistoryTimeline({ entityType, entityId, source }) {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | email
  const [replyContext, setReplyContext] = useState(null);
  const [resumeDraft, setResumeDraft] = useState(null);
  const [draftsRefreshKey, setDraftsRefreshKey] = useState(0);
  const composerOpen = !!replyContext || !!resumeDraft;
  const closeComposer = () => { setReplyContext(null); setResumeDraft(null); };
  const bumpDrafts = () => setDraftsRefreshKey(k => k + 1);

  const load = async () => {
    setLoading(true);
    try {
      const url = source || `/history?entity_type=${entityType}&entity_id=${entityId}`;
      const { data } = await api.get(url);
      setEntries(data);
    } catch {
      setEntries([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, entityId, source]);

  // Build a Reply-All context. Rules:
  //   • To    = original sender (for received) or original To (for sent — reply to self-sent case)
  //   • Cc    = all other recipients minus the current user's own email
  //   • Bcc   = empty (editable)
  //   • Subject prefixed with "Re: " (dedupe existing prefixes)
  const openReply = (email, historyEntry) => {
    const selfEmail = user?.email || "";
    const original = {
      ...email,
      timestamp: email.timestamp || historyEntry.timestamp,
    };
    const isSent = email.direction === "sent";
    const replyTo = isSent ? email.to : (email.from || "");
    // Everyone else who was on the original — the original To/Cc/From line
    // (minus reply-target, minus self) becomes the Reply-All Cc.
    const others = [];
    if (!isSent && email.to) others.push(email.to);          // people that were on To
    if (isSent && email.from) others.push(email.from);       // sender (self) — will be pruned
    if (email.cc) others.push(email.cc);
    const ccCombined = pruneAddresses(others.join(", "), selfEmail);
    // Also prune the reply-target from Cc.
    const ccFinal = pruneAddresses(ccCombined, (replyTo.match(/<([^>]+)>/)?.[1] || replyTo));
    setReplyContext({
      to: replyTo,
      cc: ccFinal,
      subject: `Re: ${stripReply(email.subject)}`,
      thread_id: email.thread_id || null,
      in_reply_to_id: email.provider_message_id || null,
      quoted_html: buildQuotedBlock(original),
    });
  };

  const emailCount = entries.filter(e => e.event_type === "email").length;
  const shown = filter === "email"
    ? entries.filter(e => e.event_type === "email")
    : entries;

  return (
    <div className="pg-card p-6" data-testid="history-timeline">
      <EmailDraftsPanel
        linkedType={entityType}
        linkedId={entityId}
        refreshKey={draftsRefreshKey}
        onResume={(d) => { setReplyContext(null); setResumeDraft(d); }}
        onChanged={bumpDrafts}
      />
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm mono uppercase text-mute tracking-wider">History</h3>
        <div className="flex items-center gap-3">
          {emailCount > 0 && (
            <div className="flex gap-1" data-testid="history-filter">
              <button
                onClick={() => setFilter("all")}
                className={`pg-btn ${filter === "all" ? "pg-btn-active" : ""}`}
                style={{ padding: "0.2rem 0.55rem", fontSize: 11 }}
              >All</button>
              <button
                onClick={() => setFilter("email")}
                className={`pg-btn ${filter === "email" ? "pg-btn-active" : ""}`}
                style={{ padding: "0.2rem 0.55rem", fontSize: 11 }}
              >Email · {emailCount}</button>
            </div>
          )}
          <span className="mono text-[11px] text-mute">{shown.length} events</span>
        </div>
      </div>

      {loading ? (
        <div className="text-mute mono text-xs">loading…</div>
      ) : shown.length === 0 ? (
        <div className="text-mute mono text-xs">No history yet</div>
      ) : (
        <ol className="relative" style={{ borderLeft: "1px solid var(--pg-border)", marginLeft: 6 }}>
          {shown.map((e, idx) => (
            <li key={e.id} className="pl-5 pr-1 py-2.5 relative" data-testid={`history-entry-${idx}`}>
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: -5, top: 14,
                  width: 9, height: 9,
                  background: "var(--pg-bg)",
                  border: `1px solid ${
                    e.event_type === "email_opened"
                      ? "var(--pg-accent-deep, #34a853)"
                      : e.event_type === "email_clicked"
                      ? "var(--pg-purple, #5B4FE8)"
                      : "var(--pg-purple)"
                  }`,
                  borderRadius: 8,
                }}
              />
              {e.event_type === "email" ? (
                <>
                  <EmailEntry e={e} onReplyClick={openReply} />
                  <div className="mono text-[11px] text-mute mt-1 pl-11" title={e.timestamp}>
                    {fmtTs(e.timestamp)}{e.user_name ? ` · via ${e.user_name}` : ""}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div className="text-sm flex-1" style={{ color: "var(--pg-text)" }}>
                      {e._source && (
                        <span className="text-mute mono text-[11px] mr-1">{e._source}</span>
                      )}
                      {e.deal_name && !e._source && (
                        <span className="text-mute mono text-[11px] mr-1">[{e.deal_name}]</span>
                      )}
                      {e.description}
                    </div>
                    <div className="mono text-[11px] text-mute whitespace-nowrap" title={e.timestamp}>
                      {fmtTs(e.timestamp)}
                    </div>
                  </div>
                  {e.user_name && (
                    <div className="mono text-[11px] text-mute mt-0.5">by {e.user_name}</div>
                  )}
                </>
              )}
            </li>
          ))}
        </ol>
      )}
      <ComposeEmailModal
        open={composerOpen}
        onClose={closeComposer}
        linkedType={entityType}
        linkedId={entityId}
        onSent={() => { load(); bumpDrafts(); }}
        onDraftChange={bumpDrafts}
        replyContext={resumeDraft ? null : replyContext}
        initialDraft={resumeDraft}
      />
    </div>
  );
}
